import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Building2, CheckCircle, ArrowRight, Star, Shield, Zap, 
  BarChart3, CreditCard, Package, Warehouse, Calculator,
  Users, Globe, Phone, ChevronDown, Play, Menu, X,
  Clock, TrendingUp, Smartphone, HeadphonesIcon, Award,
  Mail, User, MapPin, FileText, Eye, EyeOff, RefreshCw,
  MailCheck, MailX, Lock, ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Generated image URLs
const IMAGES = {
  hero: 'https://static.prod-images.emergentagent.com/jobs/d6f73905-ea8d-405a-9aa6-0e445eafff5a/images/1111911f70b307622df53997681ad9a9d7d24faff52dca899cef3560a698d597.png',
  propertyManagement: 'https://static.prod-images.emergentagent.com/jobs/d6f73905-ea8d-405a-9aa6-0e445eafff5a/images/fdf8eb4a11d0e14d0cb204b05b09f151949d143905d6fa63aa062a1e8edde116.png',
  bookingEngine: 'https://static.prod-images.emergentagent.com/jobs/d6f73905-ea8d-405a-9aa6-0e445eafff5a/images/e0a0f619c74f3c4e0015f375d1a27fcb03c6a428cc049654ab1c6bc9e8b04f86.png',
  aiPricing: 'https://static.prod-images.emergentagent.com/jobs/d6f73905-ea8d-405a-9aa6-0e445eafff5a/images/ca5eaf168e9c7571041f4a8576ff49261e32a626a87ccf41964a59867b17605f.png',
};

const FEATURES = [
  {
    icon: Building2,
    title: 'Property Management System',
    description: 'Complete hotel operations management with room inventory, housekeeping, maintenance tracking, and guest management in one unified dashboard.',
    image: IMAGES.propertyManagement,
    benefits: ['Room status tracking', 'Housekeeping management', 'Guest profiles', 'Multi-property support']
  },
  {
    icon: Globe,
    title: 'Online Booking Engine',
    description: 'Branded booking portal that lets guests search, compare, and book rooms directly. Mobile-responsive with real-time availability.',
    image: IMAGES.bookingEngine,
    benefits: ['Direct bookings', 'Mobile optimized', 'Real-time inventory', 'Custom branding']
  },
  {
    icon: TrendingUp,
    title: 'AI-Powered Dynamic Pricing',
    description: 'Intelligent pricing algorithms that analyze demand, competition, and market trends to maximize your revenue automatically.',
    image: IMAGES.aiPricing,
    benefits: ['Revenue optimization', 'Demand forecasting', 'Competitor analysis', 'Automated pricing']
  },
  {
    icon: CreditCard,
    title: 'Integrated Payment Gateway',
    description: 'Secure payment processing with multiple options including credit cards, UPI, net banking, and pay-at-hotel options.',
    benefits: ['Razorpay integration', 'Multiple payment modes', 'Secure transactions', 'Instant confirmations']
  },
  {
    icon: Package,
    title: 'Purchase Management',
    description: 'Streamline your procurement with vendor management, purchase orders, inventory tracking, and cost analysis.',
    benefits: ['Vendor management', 'Purchase orders', 'Cost tracking', 'Approval workflows']
  },
  {
    icon: Warehouse,
    title: 'Asset Management',
    description: 'Track and manage all your hotel assets including furniture, equipment, and amenities with depreciation tracking.',
    benefits: ['Asset tracking', 'Maintenance schedules', 'Depreciation', 'QR code tagging']
  },
  {
    icon: Calculator,
    title: 'Finance & Accounting',
    description: 'Complete financial management with GST-compliant invoicing, ledger accounts, and comprehensive financial reports.',
    benefits: ['GST invoicing', 'Ledger management', 'Financial reports', 'Bank reconciliation']
  },
  {
    icon: BarChart3,
    title: 'Analytics & Reports',
    description: 'Real-time dashboards and detailed reports to track occupancy, revenue, expenses, and operational efficiency.',
    benefits: ['Occupancy reports', 'Revenue analytics', 'Expense tracking', 'Custom dashboards']
  },
];

const STATS = [
  { value: '100+', label: 'Active Properties' },
  { value: '10K+', label: 'Bookings Managed' },
  { value: '4.8★', label: 'Partner Rating' },
  { value: '24/7', label: 'Support Available' },
];

const TESTIMONIALS = [
  {
    name: 'Rajesh Kumar',
    role: 'Owner, The Grand Palace',
    content: 'Since switching to this platform, our direct bookings increased by 40%. The AI pricing alone has boosted our revenue significantly.',
    rating: 5
  },
  {
    name: 'Priya Sharma',
    role: 'Manager, Seaside Resort',
    content: 'The all-in-one approach saves us hours every day. No more juggling between different software for bookings, accounting, and inventory.',
    rating: 5
  },
  {
    name: 'Mohammed Ali',
    role: 'Director, City Hotels Group',
    content: 'Managing 5 properties from a single dashboard has transformed our operations. The support team is incredibly responsive.',
    rating: 5
  },
];

const PROPERTY_TYPES = [
  { id: 'hotel', label: 'Hotel' },
  { id: 'resort', label: 'Resort' },
  { id: 'apartment', label: 'Apartment/Homestay' },
  { id: 'villa', label: 'Villa' },
  { id: 'guesthouse', label: 'Guest House' }
];

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry'
];

// Theme colors
const THEME = {
  primary: '#2563eb',
  primaryDark: '#1d4ed8',
  gradient: 'from-blue-600 to-indigo-600',
  gradientDark: 'from-blue-700 to-indigo-700',
};

export default function ListPropertyLanding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeView, setActiveView] = useState('landing'); // 'landing', 'login', 'register', 'status'
  const [registerStep, setRegisterStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  
  // Registration state
  const [formData, setFormData] = useState({
    owner_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    company_name: '',
    company_address: '',
    city: '',
    state: '',
    pincode: '',
    gst_number: '',
    pan_number: '',
    estimated_properties: 1,
    property_types: ['hotel'],
    website: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  
  // Status check state
  const [checkEmail, setCheckEmail] = useState('');
  const [statusResult, setStatusResult] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);

  // Handle email verification from URL params
  useEffect(() => {
    const verifyParam = searchParams.get('verify');
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');
    
    if (verifyParam === 'true' && emailParam && tokenParam) {
      verifyEmailToken(emailParam, tokenParam);
    }
  }, [searchParams]);

  const verifyEmailToken = async (email, token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Verification failed');
      toast.success('Email verified successfully!');
      setActiveView('status');
      setCheckEmail(email);
      checkStatus(email);
    } catch (error) {
      toast.error(error.message);
      setActiveView('status');
    } finally {
      setLoading(false);
    }
  };

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      const data = await response.json();
      
      if (!response.ok) {
        // Check for email not verified error
        if (response.status === 403 && data.detail && data.detail.error === 'email_not_verified') {
          toast.error(
            <div>
              <p className="font-semibold">Email not verified</p>
              <p className="text-sm mt-1">Please check your inbox for the verification link.</p>
              <button 
                onClick={() => handleResendVerification(loginData.email)}
                className="mt-2 text-sm underline hover:no-underline"
              >
                Resend verification email
              </button>
            </div>,
            { duration: 8000 }
          );
          setLoading(false);
          return;
        }
        
        // Check for active session (409 Conflict with session_exists error)
        if (response.status === 409 || (data.detail && data.detail.error === 'session_exists')) {
          // Handle active session - force login
          const forceResponse = await fetch(`${API}/api/auth/force-login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(loginData)
          });
          const forceData = await forceResponse.json();
          if (!forceResponse.ok) {
            // Check for email not verified in force login too
            if (forceResponse.status === 403 && forceData.detail && forceData.detail.error === 'email_not_verified') {
              toast.error('Please verify your email before logging in. Check your inbox for the verification link.', { duration: 6000 });
              setLoading(false);
              return;
            }
            const errorMsg = typeof forceData.detail === 'object' ? forceData.detail.message || 'Login failed' : forceData.detail || 'Login failed';
            throw new Error(errorMsg);
          }
          localStorage.setItem('token', forceData.token);
          localStorage.setItem('user', JSON.stringify(forceData.user));
          toast.success('Login successful!');
          // Use window.location for full page reload to ensure auth state is picked up
          window.location.href = '/admin';
          return;
        }
        // Handle other errors - extract message from detail object if needed
        const errorMsg = typeof data.detail === 'object' ? data.detail.message || 'Login failed' : data.detail || 'Login failed';
        throw new Error(errorMsg);
      }
      
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      toast.success('Login successful!');
      // Use window.location for full page reload to ensure auth state is picked up
      window.location.href = '/admin';
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Resend verification email
  const handleResendVerification = async (email) => {
    try {
      const response = await fetch(`${API}/api/portal/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (response.ok) {
        toast.success('Verification email sent! Please check your inbox.');
      } else {
        toast.error(data.detail || 'Failed to send verification email');
      }
    } catch (error) {
      toast.error('Failed to send verification email');
    }
  };

  // Registration validation
  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.owner_name.trim()) newErrors.owner_name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) newErrors.phone = 'Invalid phone number';
    if (!formData.password) newErrors.password = 'Password is required';
    else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
    if (formData.password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.company_name.trim()) newErrors.company_name = 'Company name is required';
    if (!formData.company_address.trim()) newErrors.company_address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state) newErrors.state = 'State is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    else if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Invalid pincode';
    if (formData.property_types.length === 0) newErrors.property_types = 'Select at least one property type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async () => {
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/register-property`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_name: formData.owner_name,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          company_name: formData.company_name,
          company_address: formData.company_address,
          city: formData.city,
          state: formData.state,
          pincode: formData.pincode,
          gst_number: formData.gst_number || null,
          pan_number: formData.pan_number || null,
          estimated_properties: formData.estimated_properties,
          property_types: formData.property_types,
          website: formData.website || null,
          description: formData.description || null
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Registration failed');
      toast.success('Registration submitted! Please check your email to verify.');
      setVerificationEmail(formData.email);
      setRegisterStep(4); // Success step
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const checkStatus = async (emailToCheck) => {
    const email = emailToCheck || checkEmail;
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/registration-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Not found');
      setStatusResult(data);
    } catch (error) {
      toast.error(error.message);
      setStatusResult(null);
    } finally {
      setLoading(false);
    }
  };

  const resendVerification = async () => {
    const email = verificationEmail || checkEmail;
    if (!email) {
      toast.error('Please enter your email');
      return;
    }
    setResendLoading(true);
    try {
      const response = await fetch(`${API}/api/portal/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Failed to resend');
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResendLoading(false);
    }
  };

  const handlePropertyTypeChange = (typeId) => {
    setFormData(prev => ({
      ...prev,
      property_types: prev.property_types.includes(typeId)
        ? prev.property_types.filter(t => t !== typeId)
        : [...prev.property_types, typeId]
    }));
  };

  // Reset to landing
  const backToLanding = () => {
    setActiveView('landing');
    setRegisterStep(1);
    setStatusResult(null);
    setErrors({});
  };

  // Render Login Form
  const renderLogin = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl relative">
        {/* Close Button */}
        <button
          onClick={backToLanding}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
        </button>
        <CardHeader className="text-center border-b pb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Partner Login</CardTitle>
          <CardDescription>Sign in to manage your properties</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="login-email">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="login-email"
                  type="email"
                  placeholder="you@company.com"
                  className="pl-10"
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="login-password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  className="pl-10 pr-10"
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                />
                <button
                  type="button"
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600" disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <div className="mt-6 pt-6 border-t text-center space-y-3">
            <p className="text-sm text-gray-600">Don't have an account?</p>
            <Button variant="outline" className="w-full" onClick={() => { setActiveView('register'); setRegisterStep(1); }}>
              Register Your Property
            </Button>
            <Button variant="link" className="text-sm" onClick={() => setActiveView('status')}>
              Already registered? Check application status
            </Button>
            <Button variant="ghost" className="w-full text-gray-500" onClick={backToLanding}>
              ← Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Registration Form
  const renderRegister = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <Card className="shadow-2xl relative">
          {/* Close Button */}
          <button
            onClick={backToLanding}
            className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
          </button>
          <CardHeader className="text-center border-b">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Building2 className="h-8 w-8 text-white" />
            </div>
            <CardTitle className="text-2xl">List Your Property</CardTitle>
            <CardDescription>Join our platform and reach thousands of travelers</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {/* Progress Indicator */}
            {registerStep < 4 && (
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className={registerStep >= 1 ? 'text-blue-600 font-medium' : 'text-gray-400'}>Personal</span>
                  <span className={registerStep >= 2 ? 'text-blue-600 font-medium' : 'text-gray-400'}>Company</span>
                  <span className={registerStep >= 3 ? 'text-blue-600 font-medium' : 'text-gray-400'}>Property</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300"
                    style={{ width: registerStep === 1 ? '33%' : registerStep === 2 ? '66%' : '100%' }}
                  />
                </div>
              </div>
            )}

            {/* Step 1: Personal Details */}
            {registerStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input placeholder="Enter your full name" className="pl-10" value={formData.owner_name}
                        onChange={(e) => setFormData({...formData, owner_name: e.target.value})} />
                    </div>
                    {errors.owner_name && <p className="text-xs text-red-500">{errors.owner_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Email Address *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input type="email" placeholder="you@company.com" className="pl-10" value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})} />
                    </div>
                    {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input placeholder="10-digit mobile number" className="pl-10" value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                    </div>
                    {errors.phone && <p className="text-xs text-red-500">{errors.phone}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Password *</Label>
                    <div className="relative">
                      <Input type={showPassword ? 'text' : 'password'} placeholder="Min 6 characters" className="pr-10"
                        value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} />
                      <button type="button" className="absolute right-3 top-3 text-gray-400" onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-xs text-red-500">{errors.password}</p>}
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Confirm Password *</Label>
                    <Input type="password" placeholder="Re-enter password" value={formData.confirm_password}
                      onChange={(e) => setFormData({...formData, confirm_password: e.target.value})} />
                    {errors.confirm_password && <p className="text-xs text-red-500">{errors.confirm_password}</p>}
                  </div>
                </div>
                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={backToLanding}>← Back</Button>
                  <Button onClick={() => validateStep1() && setRegisterStep(2)} className="bg-gradient-to-r from-blue-600 to-indigo-600">
                    Next: Company Details
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Company Details */}
            {registerStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label>Company/Property Name *</Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Input placeholder="Your hotel/company name" className="pl-10" value={formData.company_name}
                        onChange={(e) => setFormData({...formData, company_name: e.target.value})} />
                    </div>
                    {errors.company_name && <p className="text-xs text-red-500">{errors.company_name}</p>}
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Address *</Label>
                    <Textarea placeholder="Full address" value={formData.company_address}
                      onChange={(e) => setFormData({...formData, company_address: e.target.value})} />
                    {errors.company_address && <p className="text-xs text-red-500">{errors.company_address}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>City *</Label>
                    <Input placeholder="City" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                    {errors.city && <p className="text-xs text-red-500">{errors.city}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>State *</Label>
                    <select className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                      value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})}>
                      <option value="">Select State</option>
                      {INDIAN_STATES.map(state => <option key={state} value={state}>{state}</option>)}
                    </select>
                    {errors.state && <p className="text-xs text-red-500">{errors.state}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>Pincode *</Label>
                    <Input placeholder="6-digit pincode" value={formData.pincode}
                      onChange={(e) => setFormData({...formData, pincode: e.target.value})} />
                    {errors.pincode && <p className="text-xs text-red-500">{errors.pincode}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label>GST Number (Optional)</Label>
                    <Input placeholder="22AAAAA0000A1Z5" value={formData.gst_number}
                      onChange={(e) => setFormData({...formData, gst_number: e.target.value.toUpperCase()})} />
                  </div>
                </div>
                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={() => setRegisterStep(1)}>← Back</Button>
                  <Button onClick={() => validateStep2() && setRegisterStep(3)} className="bg-gradient-to-r from-blue-600 to-indigo-600">
                    Next: Property Details
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Property Details */}
            {registerStep === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Property Types *</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
                    {PROPERTY_TYPES.map(type => (
                      <label key={type.id} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all
                        ${formData.property_types.includes(type.id) ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300'}`}>
                        <Checkbox checked={formData.property_types.includes(type.id)} onCheckedChange={() => handlePropertyTypeChange(type.id)} />
                        <span className="text-sm font-medium">{type.label}</span>
                      </label>
                    ))}
                  </div>
                  {errors.property_types && <p className="text-xs text-red-500">{errors.property_types}</p>}
                </div>
                <div className="space-y-2">
                  <Label>Estimated Number of Properties</Label>
                  <Input type="number" min="1" value={formData.estimated_properties}
                    onChange={(e) => setFormData({...formData, estimated_properties: parseInt(e.target.value) || 1})} />
                </div>
                <div className="space-y-2">
                  <Label>Tell us about your property (Optional)</Label>
                  <Textarea placeholder="Brief description of your property" value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div className="flex justify-between pt-4">
                  <Button variant="ghost" onClick={() => setRegisterStep(2)}>← Back</Button>
                  <Button onClick={handleRegister} disabled={loading} className="bg-gradient-to-r from-green-600 to-green-700">
                    {loading ? 'Submitting...' : 'Submit Registration'}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Success */}
            {registerStep === 4 && (
              <div className="text-center py-8 space-y-6">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Mail className="h-10 w-10 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Verify Your Email</h2>
                  <p className="text-gray-600 mt-2">We've sent a verification link to <strong>{verificationEmail}</strong></p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto text-left">
                  <h3 className="font-semibold text-blue-900">Next Steps:</h3>
                  <ul className="text-sm text-blue-800 mt-2 space-y-1">
                    <li>1. Check your email inbox (and spam folder)</li>
                    <li>2. Click the verification link</li>
                    <li>3. Once verified, our team will review your application</li>
                  </ul>
                </div>
                <div className="flex flex-col gap-3">
                  <Button variant="outline" onClick={resendVerification} disabled={resendLoading}>
                    {resendLoading ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null} Resend Verification Email
                  </Button>
                  <Button variant="ghost" onClick={backToLanding}>← Back to Home</Button>
                </div>
              </div>
            )}

            {/* Already registered link */}
            {registerStep < 4 && (
              <div className="mt-6 pt-6 border-t text-center">
                <Button variant="link" onClick={() => setActiveView('status')}>
                  Already registered? Check your application status →
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );

  // Render Status Check
  const renderStatus = () => (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-2xl relative">
        {/* Close Button */}
        <button
          onClick={backToLanding}
          className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-gray-500 hover:text-gray-700" />
        </button>
        <CardHeader className="text-center border-b pb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Clock className="h-8 w-8 text-white" />
          </div>
          <CardTitle className="text-2xl">Check Application Status</CardTitle>
          <CardDescription>Enter your registered email to check status</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex gap-2">
            <Input type="email" placeholder="Enter your email" value={checkEmail}
              onChange={(e) => setCheckEmail(e.target.value)} />
            <Button onClick={() => checkStatus()} disabled={loading}>
              {loading ? 'Checking...' : 'Check'}
            </Button>
          </div>

          {statusResult && (
            <Card className={`${statusResult.status === 'approved' ? 'border-green-200 bg-green-50' : ''}
              ${statusResult.status === 'pending' ? 'border-yellow-200 bg-yellow-50' : ''}
              ${statusResult.status === 'rejected' ? 'border-red-200 bg-red-50' : ''}`}>
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  {statusResult.status === 'approved' && <CheckCircle className="h-8 w-8 text-green-600" />}
                  {statusResult.status === 'pending' && <Clock className="h-8 w-8 text-yellow-600" />}
                  {statusResult.status === 'rejected' && <X className="h-8 w-8 text-red-600" />}
                  <div>
                    <p className="font-semibold">{statusResult.company_name}</p>
                    <p className="text-sm text-gray-600">ID: {statusResult.registration_id}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Email Verified</span>
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1
                        ${statusResult.email_verified ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                        {statusResult.email_verified ? <><MailCheck className="h-3 w-3" /> Verified</> : <><MailX className="h-3 w-3" /> Not Verified</>}
                      </span>
                      {!statusResult.email_verified && (
                        <button
                          onClick={resendVerification}
                          disabled={resendLoading}
                          className="p-1.5 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 transition-colors disabled:opacity-50"
                          title="Resend verification email"
                        >
                          {resendLoading ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                          ) : (
                            <Mail className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Application Status</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium uppercase
                      ${statusResult.status === 'approved' ? 'bg-green-100 text-green-700' : ''}
                      ${statusResult.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : ''}
                      ${statusResult.status === 'rejected' ? 'bg-red-100 text-red-700' : ''}`}>
                      {statusResult.status}
                    </span>
                  </div>
                  {!statusResult.email_verified && statusResult.status === 'pending' && (
                    <Button size="sm" variant="outline" onClick={resendVerification} disabled={resendLoading} className="w-full mt-2">
                      {resendLoading ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : <Mail className="h-3 w-3 mr-1" />}
                      Resend Verification Email
                    </Button>
                  )}
                  {statusResult.status === 'approved' && (
                    <div className="mt-3 p-3 bg-green-100 rounded-lg">
                      <p className="text-sm text-green-800">🎉 Approved! <button className="underline font-medium" onClick={() => setActiveView('login')}>Login now</button></p>
                    </div>
                  )}
                  {statusResult.status === 'rejected' && statusResult.rejection_reason && (
                    <div className="mt-3 p-3 bg-red-100 rounded-lg">
                      <p className="text-sm text-red-800"><strong>Reason:</strong> {statusResult.rejection_reason}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          <div className="pt-4 border-t text-center space-y-2">
            <Button variant="outline" className="w-full" onClick={() => { setActiveView('register'); setRegisterStep(1); }}>
              Submit a New Registration
            </Button>
            <Button variant="ghost" className="w-full text-gray-500" onClick={backToLanding}>
              ← Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render Landing Page
  const renderLanding = () => (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">HotelCloud</span>
            </div>
            
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Why Us</a>
              <a href="#testimonials" className="text-gray-600 hover:text-gray-900 text-sm font-medium">Testimonials</a>
              <Button variant="link" className="text-gray-600 text-sm" onClick={() => setActiveView('status')}>
                Check Status
              </Button>
              <Button variant="ghost" onClick={() => setActiveView('login')} className="text-gray-600">Login</Button>
              <Button onClick={() => { setActiveView('register'); setRegisterStep(1); }}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                Get Started Free
              </Button>
            </div>

            <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4 space-y-3">
            <a href="#features" className="block py-2 text-gray-600">Features</a>
            <a href="#pricing" className="block py-2 text-gray-600">Why Us</a>
            <a href="#testimonials" className="block py-2 text-gray-600">Testimonials</a>
            <Button variant="link" className="w-full justify-start p-0 text-gray-600" onClick={() => { setActiveView('status'); setMobileMenuOpen(false); }}>
              Check Status
            </Button>
            <Button variant="outline" className="w-full" onClick={() => { setActiveView('login'); setMobileMenuOpen(false); }}>Login</Button>
            <Button className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
              onClick={() => { setActiveView('register'); setRegisterStep(1); setMobileMenuOpen(false); }}>
              Get Started Free
            </Button>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                <Zap className="h-4 w-4" /> All-in-One Hotel Management Platform
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Grow Your Hotel Business with{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">Smart Technology</span>
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Join hundreds of hoteliers who've transformed their operations with our comprehensive property management system.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-gradient-to-r from-blue-600 to-indigo-600 text-lg px-8 py-6"
                  onClick={() => { setActiveView('register'); setRegisterStep(1); }}>
                  List Your Property <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 py-6" onClick={() => navigate('/')}>
                  <Play className="mr-2 h-5 w-5" /> View Demo Portal
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-4">
                {STATS.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative lg:pl-8">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img src={IMAGES.hero} alt="Hotel Management Dashboard" className="w-full h-auto" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <div className="text-sm text-gray-500">Avg. Revenue Increase</div>
                    <div className="text-2xl font-bold text-gray-900">+35%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Everything You Need to Run Your Hotel</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">A complete suite of tools designed specifically for the hospitality industry.</p>
          </div>
          
          <div className="space-y-24">
            {FEATURES.slice(0, 3).map((feature, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                    <feature.icon className="h-4 w-4" /> {feature.title}
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900">{feature.title}</h3>
                  <p className="text-lg text-gray-600">{feature.description}</p>
                  <ul className="grid grid-cols-2 gap-3">
                    {feature.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-center gap-2 text-gray-700">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" /> {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="rounded-2xl overflow-hidden shadow-xl">
                    <img src={feature.image} alt={feature.title} className="w-full h-auto" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-24">
            <h3 className="text-2xl font-bold text-gray-900 text-center mb-12">Plus Many More Powerful Features</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {FEATURES.slice(3).map((feature, index) => (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center mb-4">
                      <feature.icon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h4>
                    <p className="text-gray-600 text-sm mb-4">{feature.description}</p>
                    <ul className="space-y-1">
                      {feature.benefits.slice(0, 2).map((benefit, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-green-500" /> {benefit}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-blue-900 via-indigo-900 to-purple-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why Property Owners Choose Us</h2>
            <p className="text-xl text-blue-200 max-w-3xl mx-auto">We're not just another software provider. We're your partner in hospitality success.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Clock className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Quick Setup</h3>
              <p className="text-blue-200">Get your property online in minutes, not days. Our team helps you migrate existing data quickly.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-14 h-14 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">Secure & Reliable</h3>
              <p className="text-blue-200">Bank-grade security, daily backups, and 99.9% uptime guarantee.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="w-14 h-14 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl flex items-center justify-center mb-6">
                <HeadphonesIcon className="h-7 w-7 text-white" />
              </div>
              <h3 className="text-xl font-bold mb-3">24/7 Support</h3>
              <p className="text-blue-200">Dedicated support team available round the clock via phone, email, or chat.</p>
            </div>
          </div>
          
          <div className="mt-16 text-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-10 py-6"
              onClick={() => { setActiveView('register'); setRegisterStep(1); }}>
              Start Your Free Trial <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-4 text-blue-200 text-sm">No credit card required • Free setup assistance • Cancel anytime</p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Trusted by Hoteliers Across India</h2>
            <p className="text-xl text-gray-600">See what our customers have to say.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />)}
                  </div>
                  <p className="text-gray-700 mb-6 leading-relaxed">"{testimonial.content}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">Ready to Transform Your Hotel Business?</h2>
          <p className="text-xl text-blue-100 mb-8">Join hundreds of successful hoteliers. List your property today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-10 py-6"
              onClick={() => { setActiveView('register'); setRegisterStep(1); }}>
              List Your Property Now
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 text-lg px-10 py-6"
              onClick={() => window.open('tel:+919999999999')}>
              <Phone className="mr-2 h-5 w-5" /> Talk to Sales
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-xl flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white">HotelCloud</span>
              </div>
              <p className="text-sm">The all-in-one hotel management platform trusted by hundreds of properties across India.</p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Features</a></li>
                <li><a href="#pricing" className="hover:text-white">Pricing</a></li>
                <li><a href="#" className="hover:text-white">Integrations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white">About Us</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
                <li><a href="#" className="hover:text-white">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => { setActiveView('register'); setRegisterStep(1); }} className="hover:text-white">List Your Property</button></li>
                <li><button onClick={() => setActiveView('login')} className="hover:text-white">Partner Login</button></li>
                <li><button onClick={() => setActiveView('status')} className="hover:text-white">Check Status</button></li>
                <li><button onClick={() => navigate('/')} className="hover:text-white">Booking Portal</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm">
            <p>© 2024 HotelCloud. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );

  // Main render
  if (activeView === 'login') return renderLogin();
  if (activeView === 'register') return renderRegister();
  if (activeView === 'status') return renderStatus();
  return renderLanding();
}
